import React from 'react'
import { PieTitle, PieLegend, PieLegendItem, PieColorSwatch, PieLegendLabel, PieLegendValue } from './SupervisionStyle'
import { getPrecisePercentages, formatPercentage } from '../../../../helpers/getPrecisePercentages'

// Simple, self-contained data model for the pie
export type PieSlice = {
  label: string
  value: number
  color: string
  count?: number // optional raw count to show in legend
}

type Props = {
  title?: string
  data: PieSlice[]
}

// ─── Custom SVG Pie Chart ────────────────────────────────────────────────────

interface SVGPieProps {
  slices: { label: string; value: number; color: string; pct: number }[]
  size?: number
}

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const angleRad = ((angleDeg - 90) * Math.PI) / 180
  return {
    x: cx + r * Math.cos(angleRad),
    y: cy + r * Math.sin(angleRad),
  }
}

function describeArc(cx: number, cy: number, r: number, startAngle: number, endAngle: number) {
  const sweep = endAngle - startAngle
  if (sweep >= 359.99) {
    // For a full circle, SVG arcs fail if start/end points are identical.
    // We return a path that draws a full circle.
    return `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx} ${cy + r} A ${r} ${r} 0 1 1 ${cx} ${cy - r} Z`
  }
  const start = polarToCartesian(cx, cy, r, endAngle)
  const end = polarToCartesian(cx, cy, r, startAngle)
  const largeArcFlag = sweep <= 180 ? '0' : '1'
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y} Z`
}

const ELBOW_LEN = 26 // radial distance from arc edge to pointer elbow
const TICK_LEN = 24 // horizontal tick length after the elbow

const SVGPie: React.FC<SVGPieProps> = ({ slices, size = 300 }) => {
  const padX = 210 // room for pointer + text on each side (must fit longest label)
  const padY = 100 // room for labels that get pushed above/below the pie
  const totalW = size + padX * 2
  const totalH = size + padY * 2
  const cx = totalW / 2
  const cy = totalH / 2
  const radius = size / 2

  let cumAngle = 0
  const segments = slices.map(s => {
    const startAngle = cumAngle
    const sweepAngle = (s.pct / 100) * 360
    cumAngle += sweepAngle
    const midAngle = startAngle + sweepAngle / 2
    return { ...s, startAngle, sweepAngle, midAngle }
  })

  // Build pointer geometry for every slice with pct >= 1
  const labeled = segments
    .filter(seg => seg.pct >= 1)
    .map(seg => {
      // Where the pointer starts — midpoint of the outer arc edge
      const arcEdge = polarToCartesian(cx, cy, radius, seg.midAngle)
      // Elbow: straight out from the arc edge along the midAngle direction
      const elbow = polarToCartesian(cx, cy, radius + ELBOW_LEN, seg.midAngle)
      const isRight = elbow.x >= cx
      // Horizontal tick end (where the label text is anchored)
      const tickEndX = elbow.x + (isRight ? TICK_LEN : -TICK_LEN)
      return {
        ...seg,
        arcEdge,
        elbowX: elbow.x,
        elbowY: elbow.y, // original elbow y — pointer always bends here
        // labelY starts at elbowY; adjusted below to prevent vertical overlap
        labelY: elbow.y,
        tickEndX,
        isRight,
      }
    })

  // Push labels apart vertically if they're too close on the same side
  const minGap = 28
  const adjustSide = (items: typeof labeled) => {
    items.sort((a, b) => a.labelY - b.labelY)
    for (let i = 1; i < items.length; i++) {
      if (items[i].labelY - items[i - 1].labelY < minGap) {
        items[i].labelY = items[i - 1].labelY + minGap
      }
    }
  }

  const rightLabels = labeled.filter(l => l.isRight)
  const leftLabels = labeled.filter(l => !l.isRight)
  adjustSide(rightLabels)
  adjustSide(leftLabels)

  const allLabels = [...rightLabels, ...leftLabels]

  return (
    <svg width="100%" viewBox={`0 0 ${totalW} ${totalH}`} style={{ display: 'block', overflow: 'visible' }}>
      {/* Pie slices */}
      {segments.map((seg, i) => (
        <path
          key={i}
          d={describeArc(cx, cy, radius, seg.startAngle, seg.startAngle + seg.sweepAngle)}
          fill={seg.color}
          stroke="#fff"
          strokeWidth={2}
        />
      ))}

      {/* Pass 1: all pointer lines (drawn first, underneath labels) */}
      {allLabels.map((seg, i) => (
        <polyline
          key={`line-${i}`}
          points={`${seg.arcEdge.x},${seg.arcEdge.y} ${seg.elbowX},${seg.elbowY} ${seg.tickEndX},${seg.labelY}`}
          fill="none"
          stroke={seg.color}
          strokeWidth={2}
          strokeLinejoin="round"
        />
      ))}

      {/* Pass 2: all label text on top with white halo to block any crossing lines */}
      {allLabels.map((seg, i) => {
        const textAnchor = seg.isRight ? 'start' : 'end'
        const textX = seg.isRight ? seg.tickEndX + 4 : seg.tickEndX - 4
        return (
          <text
            key={`label-${i}`}
            x={textX}
            y={seg.labelY + 4}
            textAnchor={textAnchor}
            fontSize={20}
            fontWeight={600}
            fill="#3A3A3C"
            stroke="white"
            strokeWidth={4}
            paintOrder="stroke"
            fontFamily="inherit"
          >
            {seg.label} {formatPercentage(seg.pct)}%
          </text>
        )
      })}
    </svg>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────

const SupervisionPie: React.FC<Props> = ({ title = 'Supervision compliance', data }) => {
  const [visible, setVisible] = React.useState<boolean[]>(() => data.map(() => true))

  // Keep visibility array in sync when data length changes
  React.useEffect(() => {
    setVisible(prev => {
      if (prev.length === data.length) return prev
      const next = data.map((_, i) => (i < prev.length ? prev[i] : true))
      return next
    })
  }, [data])

  // fullTotal not needed for SVG rendering (percentages computed from visible slices)

  // Filter slices by visibility for the chart
  const visibleData = React.useMemo(() => data.filter((_, i) => visible[i]), [data, visible])

  // Full-data percentages — used by both chart labels and legend (never recalculated on hide)
  const precisePercentages = React.useMemo(() => {
    return getPrecisePercentages(data.map(s => s.value), 0)
  }, [data])

  // Map each visible slice back to its original index
  const visibleOriginalIndices = React.useMemo(() => {
    const indices: number[] = []
    visible.forEach((v, i) => { if (v) indices.push(i) })
    return indices
  }, [visible])

  const svgSlices = visibleData.map((s, i) => ({
    label: s.label,
    value: s.value,
    color: s.color,
    pct: precisePercentages[visibleOriginalIndices[i]], // always full-data %
  }))

  return (
    <div>
      {title ? <PieTitle>{title}</PieTitle> : null}

      {/* SVG-based pie chart with outside labels */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 0 }}>
        <SVGPie slices={svgSlices} />
      </div>

      {/* Custom legend */}
      <PieLegend>
        {data.map((s, idx) => (
          <PieLegendItem
            key={idx}
            onClick={() => {
              const visibleCount = visible.filter(Boolean).length
              if (visible[idx] && visibleCount <= 1) return
              setVisible(v => v.map((flag, i) => (i === idx ? !flag : flag)))
            }}
            style={{ cursor: 'pointer', opacity: visible[idx] ? 1 : 0.5 }}
          >
            <PieColorSwatch bg={s.color} />
            <PieLegendLabel>{s.label}</PieLegendLabel>
            <PieLegendValue>
              {s.count != null
                ? `${s.count} (${formatPercentage(precisePercentages[idx])}%)`
                : `${formatPercentage(precisePercentages[idx])}%`}
            </PieLegendValue>
          </PieLegendItem>
        ))}
      </PieLegend>
    </div>
  )
}

export default SupervisionPie
